/**
 * Inline SVG icon set.
 *
 * These replace Unicode emoji / dingbat glyphs (🏙️ ✨ 👥 🎛️ 🔐 🧾 ☀ ☾ ✦ 🇵🇰)
 * which render inconsistently — or not at all — across devices, browsers and
 * fonts (notably flag emoji on Windows). Every line icon draws with
 * `currentColor`, so it inherits the color/animation already set on its
 * container class in the stylesheets.
 */

const baseProps = (props) => ({
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
  ...props,
});

/* 🏙️ Multi-location — city skyline */
export function IconBuildings(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M3 21h18" />
      <path d="M5 21V8l5-3v16" />
      <path d="M10 21V11l5 3v7" />
      <path d="M15 21V9l4 2.5V21" />
      <path d="M7.5 9.5v0M7.5 12.5v0M7.5 15.5v0M12.5 14v0M12.5 17v0" />
    </svg>
  );
}

/* ✨ AI picks — sparkles */
export function IconSparkles(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M12 3.5c.4 3.6 1.9 5.1 5.5 5.5-3.6.4-5.1 1.9-5.5 5.5-.4-3.6-1.9-5.1-5.5-5.5 3.6-.4 5.1-1.9 5.5-5.5Z" />
      <path d="M18.5 14.5c.2 1.6.9 2.3 2.5 2.5-1.6.2-2.3.9-2.5 2.5-.2-1.6-.9-2.3-2.5-2.5 1.6-.2 2.3-.9 2.5-2.5Z" />
    </svg>
  );
}

/* 👥 Group ordering — two people */
export function IconUsers(props) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6" />
      <path d="M17 14.2a5.5 5.5 0 0 1 3.5 5.3" />
    </svg>
  );
}

/* 🎛️ Configurator — sliders */
export function IconSliders(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M4 7h10M18 7h2" />
      <path d="M4 12h2M10 12h10" />
      <path d="M4 17h12M20 17h0" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="18" cy="17" r="2" />
    </svg>
  );
}

/* 🔐 Role-based access — padlock */
export function IconLock(props) {
  return (
    <svg {...baseProps(props)}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
      <path d="M12 14.5v2.5" />
    </svg>
  );
}

/* 🧾 Audit trail — receipt */
export function IconReceipt(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M6 3.5h12v17l-2.4-1.4-2.4 1.4-2.4-1.4-2.4 1.4L6 20.5Z" />
      <path d="M9 8h6M9 11.5h6M9 15h3.5" />
    </svg>
  );
}

/* ☀ Sun */
export function IconSun(props) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3" />
    </svg>
  );
}

/* ☾ Moon */
export function IconMoon(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
    </svg>
  );
}

/* ✦ Spark — four-pointed star (filled) */
export function IconSpark(props) {
  return (
    <svg
      {...baseProps({
        ...props,
        fill: 'currentColor',
        stroke: 'none',
        strokeWidth: undefined,
        strokeLinecap: undefined,
        strokeLinejoin: undefined,
      })}
    >
      <path d="M12 1.5c.7 5.6 4.4 9.3 10 10-5.6.7-9.3 4.4-10 10-.7-5.6-4.4-9.3-10-10 5.6-.7 9.3-4.4 10-10Z" />
    </svg>
  );
}

/* 🍽 Dining — fork & knife */
export function IconDining(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M8 3v4a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3" />
      <path d="M10 9v12" />
      <path d="M16 3v18" />
    </svg>
  );
}

/* 🛏 Bed / room service */
export function IconBed(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M2 9V5a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v4" />
      <path d="M2 9h20" />
      <path d="M2 9v8a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V9" />
      <path d="M6 9V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
      <path d="M2 17v2M22 17v2" />
    </svg>
  );
}

/* 🏨 Hotel key-card */
export function IconHotel(props) {
  return (
    <svg {...baseProps(props)}>
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M3 10h18" />
      <circle cx="16.5" cy="14.5" r="1.5" />
    </svg>
  );
}

/* 📱 QR scan */
export function IconQR(props) {
  return (
    <svg {...baseProps(props)}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h1.5v1.5H14zM17 14h4M14 17h1.5M17 17h1.5v1.5M20.5 17v3.5H17" />
    </svg>
  );
}

/* 🔔 Bell / concierge */
export function IconBell(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M6 8a6 6 0 0 1 12 0c0 4 2 6 2 6H4s2-2 2-6" />
      <path d="M10.3 21a2 2 0 0 0 3.4 0" />
    </svg>
  );
}

/* 📦 Batch ordering — stacked trays */
export function IconBatchOrder(props) {
  return (
    <svg {...baseProps(props)}>
      <rect x="3" y="14" width="18" height="5" rx="1.5" />
      <rect x="3" y="9" width="18" height="4" rx="1.5" />
      <rect x="3" y="5" width="18" height="3" rx="1.5" />
      <path d="M7 16.5h2M7 11.5h2M7 6.5h2" />
    </svg>
  );
}

/* 🚗 Car / curbside ordering */
export function IconCar(props) {
  return (
    <svg {...baseProps(props)}>
      <path d="M5.5 11L7.5 5h9l2 6" />
      <rect x="2" y="11" width="20" height="7" rx="2" />
      <circle cx="6.5" cy="18" r="1.5" />
      <circle cx="17.5" cy="18" r="1.5" />
      <path d="M2 14.5h20" />
      <path d="M10 8h4" />
    </svg>
  );
}

/* 📍 Geo-fencing — pin with radius ring */
export function IconGeoFence(props) {
  return (
    <svg {...baseProps(props)}>
      <circle cx="12" cy="9.5" r="2.5" />
      <path d="M12 2.5a7 7 0 0 1 7 7c0 4-7 12-7 12S5 13.5 5 9.5a7 7 0 0 1 7-7Z" />
      <ellipse cx="12" cy="20" rx="5" ry="1.5" strokeDasharray="2 2" />
    </svg>
  );
}

/* 🇵🇰 Flag of Pakistan */
export function IconFlagPK(props) {
  return (
    <svg
      width="1.15em"
      height="1em"
      viewBox="0 0 30 20"
      role="img"
      aria-label="Pakistan"
      focusable="false"
      {...props}
    >
      <rect width="30" height="20" rx="1.5" fill="#01411C" />
      <rect width="7.5" height="20" rx="1.5" fill="#F7F2E6" />
      <path
        d="M22.2 13.4a4.6 4.6 0 1 1 0-6.8 5.6 5.6 0 1 0 0 6.8Z"
        fill="#F7F2E6"
      />
      <path
        d="m23.4 7.1.9 1.9 2.1.2-1.6 1.4.5 2-1.9-1.1-1.8 1.1.5-2-1.6-1.4 2.1-.2.8-1.9Z"
        fill="#F7F2E6"
      />
    </svg>
  );
}
